import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { STATUS_COLORS } from './constants';

export const AssessmentsTab = ({ assessmentNotifs, assessments }) => (
  <>
    {assessmentNotifs.length > 0 && (
      <div style={{ marginBottom: '1rem' }}>
        <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#002A38', marginBottom: '0.625rem' }}>Pending Assessments</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {assessmentNotifs.map(n => (
            <Card key={n._id} style={{ background: 'rgba(43,108,176,0.04)', border: '1px solid rgba(43,108,176,0.15)', borderRadius: 12, padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#002A38' }}>{n.data?.assessment_title}</div>
                <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{n.data?.job_title}</div>
              </div>
              <Button data-testid="take-assessment-button" onClick={() => window.open(`/take-assessment/${n.data?.access_code}`, '_blank')}
                style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 }}>
                Take Assessment
              </Button>
            </Card>
          ))}
        </div>
      </div>
    )}

    {assessments.length === 0 && assessmentNotifs.length === 0 ? (
      <div className="empty-state" style={{ paddingTop: '3rem' }}>
        <p>No assessments taken yet. You'll receive assessment links from recruiters after applying.</p>
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {assessments.map(att => {
          const sc = STATUS_COLORS[att.status] || STATUS_COLORS.applied;
          return (
            <Card key={att._id} style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 12, padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div>
                  <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, color: '#002A38' }}>{att.assessment_title}</h3>
                  <p style={{ fontSize: '0.75rem', color: '#64748B' }}>{att.job_title}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {att.percentage != null && (
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: '1rem', fontWeight: 700, color: att.percentage >= 70 ? '#059669' : att.percentage >= 50 ? '#D97706' : '#DC2626' }}>
                      {att.percentage}%
                    </span>
                  )}
                  <Badge style={{ background: sc.bg, color: sc.color, borderRadius: 999, fontSize: '0.6875rem', border: 'none' }}>{att.status}</Badge>
                </div>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                {att.started_at && `Started: ${new Date(att.started_at).toLocaleDateString()}`}
                {att.total_score != null && ` | Score: ${att.total_score}/${att.total_possible}`}
              </div>
            </Card>
          );
        })}
      </div>
    )}
  </>
);

export const InterviewsTab = ({ interviews }) => {
  if (interviews.length === 0) {
    return (
      <div className="empty-state" style={{ paddingTop: '3rem' }}>
        <p>No interviews conducted yet. Recruiters will share interview links after screening.</p>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {interviews.map(sess => {
        const sc = STATUS_COLORS[sess.status] || STATUS_COLORS.applied;
        return (
          <Card key={sess._id} style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 12, padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div>
                <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, color: '#002A38' }}>{sess.interview_type}</h3>
                <p style={{ fontSize: '0.75rem', color: '#64748B' }}>{sess.job_title}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {sess.summary?.overall_score != null && (
                  <span style={{ fontFamily: 'JetBrains Mono', fontSize: '1rem', fontWeight: 700, color: sess.summary.overall_score >= 70 ? '#059669' : '#D97706' }}>
                    {sess.summary.overall_score}%
                  </span>
                )}
                <Badge style={{ background: sc.bg, color: sc.color, borderRadius: 999, fontSize: '0.6875rem', border: 'none' }}>{sess.status}</Badge>
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#94A3B8' }}>
              {sess.started_at && `Date: ${new Date(sess.started_at).toLocaleDateString()}`}
              {sess.summary?.overall_rating && ` | Rating: ${sess.summary.overall_rating}`}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export const CredentialsTab = ({ tokens }) => {
  if (tokens.length === 0) {
    return (
      <div className="empty-state" style={{ paddingTop: '3rem' }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 48, height: 48 }}>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>
        <p style={{ marginTop: '1rem' }}>No credential tokens issued yet. Complete assessments and interviews to earn your verified credential.</p>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {tokens.map(t => {
        const readiness = t.readiness === 'fully_verified' ? { color: '#059669', bg: '#ECFDF5', label: 'Fully Verified' } : t.readiness === 'partially_verified' ? { color: '#0076A3', bg: '#FFE7D9', label: 'Partially Verified' } : { color: '#D97706', bg: '#FFFBEB', label: 'Initial' };
        return (
          <Card key={t._id} style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <div>
                <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, color: '#0076A3' }}>CHIRE Credential Token</h3>
                <p style={{ fontSize: '0.75rem', color: '#718096' }}>{t.job_title || 'General Assessment'} &middot; {t.stages_completed}/4 stages</p>
              </div>
              <Badge style={{ background: readiness.bg, color: readiness.color, borderRadius: 999, fontSize: '0.6875rem', border: 'none' }}>{readiness.label}</Badge>
            </div>
            <div style={{ fontSize: '0.6875rem', color: '#A0AEC0', marginBottom: '0.75rem', fontFamily: 'JetBrains Mono' }}>
              TX: {t.blockchain?.transaction_hash?.slice(0, 24)}...
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button onClick={() => window.open(`/credentials/${t.token_id}`, '_blank')} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 }}>View Credential</Button>
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/credentials/${t.token_id}`); toast.success('Share link copied!'); }} style={{ borderRadius: 8, fontSize: '0.75rem' }}>Copy Share Link</Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
